import './App.css';
import './dark.css';
import { useEffect, useRef, useState } from 'react';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css';
import 'primereact/resources/primereact.css';
import { Chart } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { PrimeReactProvider } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { RouterProvider } from 'react-router-dom';

import { VITE_CLARITY_PROJECT_ID } from '@/core/constants';
import { NotificationBadgeContext } from '@/core/NotificationBadgeContext';
import router from '@/core/routes';
import { applyTheme, getInitialTheme } from '@/core/theme';
import { ThemeProvider } from '@/core/ThemeContext';
import { ToastContext, ToastWithRef } from '@/core/ToastContext';
import { AppVersionProvider } from '@/core/version/AppVersionContext';
import ActiveTasksBadge from '@/modules/imaging/components/ActiveTasksBadge';
import { TranscriptionTasksSync } from '@modules/imaging/hooks/useTranscriptionTasksSync';
import { ReportImageProvider } from '@modules/imaging/viewer/study/ReportImageStore';

import ChatUpdater from './ChatUpdater';
import imagenmedRouter from './imagenmedRoutes';

applyTheme(getInitialTheme());

// Inicializar Clarity dinámicamente si está definida la variable de entorno
const CLARITY_PROJECT_ID = VITE_CLARITY_PROJECT_ID;

// const localeES = require("./locales/es.json");

// // Initialize Clarity with your project ID
// const projectId = "tv8txj3zlw";
// Clarity.init(projectId);

locale('es');
addLocale('es', {
  firstDayOfWeek: 1,
  dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
  dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
  monthNames: [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ],
  monthNamesShort: [
    'ene',
    'feb',
    'mar',
    'abr',
    'may',
    'jun',
    'jul',
    'ago',
    'sep',
    'oct',
    'nov',
    'dic',
  ],
  today: 'Hoy',
  clear: 'Limpiar',
  emptyFilterMessage: 'Sin opciones disponibles',
  emptyMessage: 'No se han encontrado resultados',
  emptySearchMessage: 'Sin opciones disponibles',
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 10 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  // useEffect(() => {
  //   const updateServiceWorker = (registration) => {
  //     if (registration.waiting) {
  //       registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  //       registration.waiting.addEventListener('statechange', (event) => {
  //         console.log("UpdateServiceWorker", event);
  //         if (event.target.state === 'activated') {
  //           console.log("activated");
  //           // window.location.reload();
  //         }
  //       });
  //     }
  //   };

  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.register('/service-worker.js').then((registration) => {
  //       console.log("firstInstall", registration);
  //       registration.addEventListener('updatefound', () => {
  //         console.log(registration)
  //         updateServiceWorker(registration);
  //       });
  //     });
  //   }
  // }, []);
  // Resetear el flag de refresco exitoso si la aplicación logró cargar y montar correctamente
  useEffect(() => {
    if (sessionStorage.getItem('vite-chunk-failed') === 'true') {
      sessionStorage.removeItem('vite-chunk-failed');
    }
  }, []);

  useEffect(() => {
    Chart.register(ChartDataLabels);
    if (!CLARITY_PROJECT_ID) return;

    let cancelled = false;

    (async () => {
      try {
        const Clarity = await import('@microsoft/clarity');
        if (cancelled) return;
        if (Clarity && typeof Clarity.init === 'function') {
          Clarity.init(CLARITY_PROJECT_ID);
          console.log('Clarity initialized');
        }
      } catch (err) {
        console.warn('Failed to load Clarity', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Prefetch ImagenMed DNISearch component so the route loads faster
  useEffect(() => {
    import('@modules/imaging/ImagenMed/DNISearch').catch(() => {});
  }, []);

  const toastRef = useRef(null);

  const notificationState = useState(JSON.parse(localStorage.getItem('notificationState')) || {});

  // Decide which router to use: imagenmed minimal router (for subdomain) or full app router
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isImagenmedHost = hostname === 'imagenmed.citamed.app' || hostname.startsWith('imagenmed.');
  const isImagenmedEnv = import.meta.env && import.meta.env.VITE_IMAGENMED === 'true';
  const useImagenmed = isImagenmedEnv || isImagenmedHost;
  const routerToUse = useImagenmed ? imagenmedRouter : router;

  return (
    <PrimeReactProvider>
      <AppVersionProvider>
        <ThemeProvider>
          <QueryClientProvider client={queryClient}>
            <ToastContext.Provider value={toastRef}>
              <NotificationBadgeContext.Provider value={notificationState}>
                <ReportImageProvider>
                  <ToastWithRef />
                  <ChatUpdater />
                  <RouterProvider router={routerToUse} />
                  <ActiveTasksBadge /> {/* Notificacion de tareas activas (transcripción) */}
                  <TranscriptionTasksSync />{' '}
                  {/* NUEVO: dueño de las conexiones SSE de status de transcripción */}
                </ReportImageProvider>
              </NotificationBadgeContext.Provider>
            </ToastContext.Provider>
          </QueryClientProvider>
        </ThemeProvider>
      </AppVersionProvider>
    </PrimeReactProvider>
  );
}

export default App;
